import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Shield, Lock, Users, Database, FileText, Mail } from 'lucide-react';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="privacy-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#00D95F] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg sm:text-xl text-gray-600">for GiriChat, a service of 6Miles</p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: January 27, 2026 | Last Updated: January 27, 2026</p>
        </div>
      </div>

      <div className="flex-1 py-8 sm:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="mb-8 sm:mb-12">
            <p className="text-base sm:text-lg text-gray-700 mb-4 leading-relaxed">
              Welcome to Giri, a product of <strong>6Miles.io</strong>. Giri provides WhatsApp Business Platform solutions to help businesses ("Partners") engage with their customers. This Privacy Policy explains how we collect, use, and protect data when Partners use our services, including our "Instant Setup" features.
            </p>
          </div>

          {/* Section 1: Introduction & Roles */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              1. Introduction & Roles
            </h2>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Giri acts as a <strong>Business Solution Provider (BSP)</strong>. Under data protection laws (like GDPR/CCPA):
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-5">
                <div className="w-10 h-10 bg-[#00D95F] rounded-lg flex items-center justify-center mb-3">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">6Miles.io (Giri)</h3>
                <p className="text-sm text-gray-700">Is the <strong>Data Processor</strong>. We process data on behalf of our Partners.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">The Partner</h3>
                <p className="text-sm text-gray-700">Is the <strong>Data Controller</strong>. You decide why and how your customers' data is used.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-[#1877F2] rounded-lg flex items-center justify-center mb-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="white"/>
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Meta (WhatsApp)</h3>
                <p className="text-sm text-gray-700">Is a <strong>sub-processor</strong> that provides the underlying infrastructure.</p>
              </div>
            </div>
          </section>

          {/* Section 2: Information We Collect */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Database className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              2. Information We Collect
            </h2>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              We collect information through three primary channels:
            </p>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">A. Partner Information (Direct)</h3>
                <p className="text-gray-700 mb-3">To provide the Giri service, we collect:</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm sm:text-base">
                  <li>Contact details (Name, Email, Phone number)</li>
                  <li>Meta Business Manager ID & WhatsApp Business Account (WABA) details</li>
                  <li>Billing and payment information</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">B. Partner Data for "Instant Setup"</h3>
                <p className="text-gray-700 mb-3">To enable Instant Account Setup on Giri, we receive data from our Partners about their current business profile. This includes:</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm sm:text-base">
                  <li>Business names, addresses, and websites</li>
                  <li>Phone numbers intended for WhatsApp registration</li>
                  <li>Verification documents required by Meta for business approval</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">C. End-User Data (The Partner's Customers)</h3>
                <p className="text-gray-700 mb-3">When customers interact with a Partner via Giri, we process:</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm sm:text-base">
                  <li><strong>Message Content:</strong> Text, images, and media sent via WhatsApp (End-to-End encrypted by Meta, but accessible to the Partner through our dashboard)</li>
                  <li><strong>Metadata:</strong> Phone numbers, timestamps, and message delivery status</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3: How We Use Data */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              3. How We Use Data
            </h2>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              We use the collected information solely to:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-[#E8F9EE] rounded-lg border border-[#00D95F]/20">
                <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Facilitate Instant Onboarding</span>
                  <p className="text-sm text-gray-600 mt-1">Automate the connection between the Partner's business and the WhatsApp Business API</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-[#E8F9EE] rounded-lg border border-[#00D95F]/20">
                <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Enable Messaging</span>
                  <p className="text-sm text-gray-600 mt-1">Route messages between the Partner and their customers</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-[#E8F9EE] rounded-lg border border-[#00D95F]/20">
                <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Service Maintenance</span>
                  <p className="text-sm text-gray-600 mt-1">Troubleshoot technical issues and monitor API performance</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-[#E8F9EE] rounded-lg border border-[#00D95F]/20">
                <div className="w-6 h-6 rounded-full bg-[#00D95F] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Compliance</span>
                  <p className="text-sm text-gray-600 mt-1">Ensure all messaging adheres to Meta's WhatsApp Business Terms</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Data Sharing */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              4. Data Sharing & Third Parties
            </h2>
            
            <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-4 mb-6">
              <p className="text-gray-900 font-semibold">🔒 We do not sell data.</p>
            </div>

            <p className="text-gray-700 mb-4 leading-relaxed">
              We only share data with:
            </p>

            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                <span className="text-gray-700"><strong>Meta/WhatsApp:</strong> To register your account and deliver messages</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                <span className="text-gray-700"><strong>Sub-processors:</strong> Cloud hosting providers (e.g., AWS/Google Cloud) and payment processors required to run the Giri infrastructure</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#00D95F] rounded-full mt-2"></div>
                <span className="text-gray-700"><strong>Legal Requirements:</strong> If required by law to comply with a subpoena or similar legal process</span>
              </li>
            </ul>
          </section>

          {/* Section 5: Partner Responsibilities */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">5. Partner Responsibilities (The "Opt-In" Rule)</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6 mb-6">
              <p className="text-gray-800 mb-4">
                <strong>⚠️ Important:</strong> As a Partner using Giri, you are responsible for ensuring that your customers have given <strong>explicit consent</strong> to be contacted on WhatsApp.
              </p>
              <ul className="list-disc pl-5 text-gray-700 space-y-2">
                <li>You <strong>must maintain records</strong> of customer opt-ins</li>
                <li>Giri provides tools to manage "Unsubscribe" or "STOP" requests, which you <strong>must honor immediately</strong></li>
              </ul>
            </div>
          </section>

          {/* Section 6: Data Retention & Security */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Lock className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              6. Data Retention & Security
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Retention</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We retain Partner data for the duration of the subscription. End-user message logs are retained according to the Partner's configuration or as required for technical auditing.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Security</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We use industry-standard encryption (<strong>AES-256</strong>) for data at rest and <strong>HTTPS</strong> for data in transit.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Your Rights */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">7. Your Rights</h2>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Depending on your location, you (and your customers) may have the right to:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Access</h4>
                <p className="text-sm text-gray-600">Access the personal data we hold</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Deletion</h4>
                <p className="text-sm text-gray-600">Request deletion of data ("Right to be Forgotten")</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Withdraw Consent</h4>
                <p className="text-sm text-gray-600">Withdraw consent at any time</p>
              </div>
            </div>

            <p className="text-gray-700">
              For any such requests, please contact our Data Protection Officer at{' '}
              <a href="mailto:privacy@6miles.io" className="text-[#00D95F] hover:underline font-semibold">privacy@6miles.io</a>
            </p>
          </section>

          {/* Section 8: Changes to Policy */}
          <section className="mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">8. Changes to This Policy</h2>
            
            <p className="text-gray-700 leading-relaxed">
              We may update this policy to reflect changes in Meta's requirements or privacy laws. Partners will be notified of significant changes via email or through the Giri dashboard.
            </p>
          </section>

          {/* Contact Section */}
          <section className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-[#00D95F]" />
              Contact Us
            </h2>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] border border-[#00D95F]/20 rounded-lg p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Data Protection Officer</h4>
                  <p className="text-gray-700"><a href="mailto:privacy@6miles.io" className="text-[#00D95F] hover:underline">privacy@6miles.io</a></p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">General Inquiries</h4>
                  <p className="text-gray-700"><a href="mailto:info@6miles.io" className="text-[#00D95F] hover:underline">info@6miles.io</a></p>
                </div>
                <div className="md:col-span-2">
                  <h4 className="font-semibold text-gray-900 mb-3">Mailing Address</h4>
                  <p className="text-gray-700">
                    6Miles<br />
                    A32 Juhu Sun N Sea bldg<br />
                    Juhu Tara Rd, Santacruz West<br />
                    Mumbai 400049, India
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
